/* eslint-disable @typescript-eslint/no-unused-vars */
import { SwitchWithText } from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import { Button, CircularProgress, Collapse, Hidden } from '@material-ui/core'
import FilterListIcon from '@material-ui/icons/FilterList'
import RefreshIcon from '@material-ui/icons/Refresh'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroller'

import PageHeader from '../../components/PageHeader'
import SectionTitle from '../../components/SectionTitle'
import {
  MONITORING_UPDATE_ACCOMMODATION_QUERY,
  MONITORING_UPDATE_RATE_PLAN_QUERY,
  MONITORING_UPDATE_ROOM_TYPE_QUERY
} from '../../graphql/monitoringUpdate'
import { DateObject, addDaysToDate, getTodayDate } from '../../helpers/date'
import { getTranslate } from '../../localization'
import {
  MonitoringUpdateAccommodationLayerResponse,
  MonitoringUpdateRatePlanrResponse,
  MonitoringUpdateRoomTypeLayerResponse
} from '../../types/monitoringUpdate'
import InventoryDateRow from '../Inventory/InventoryDateRow'
import MonitoringUpdateSkeleton from './MonitoringSkeleton'
import MonitoringUpdateBoxRow from './MonitoringUpdateBoxRow'
import MonitoringUpdateFilters from './MonitoringUpdateFilters'
import useStyles from './Styles/index.style'

type RangeType = {
  label: string
  value: 'TWO_WEEKS' | 'MONTH'
}
export default function MonitoringUpdate() {
  const classes = useStyles()
  const [showChannels, setShowChannels] = useState<boolean>(false)
  const [showCrsAndPms, setShowCrsAndPms] = useState<boolean>(false)
  const PAGE_SIZE = 10
  const [numberOfVisibleAccommodations, setnumberOfVisibleAccommodations] =
    useState(PAGE_SIZE)
  const [inventoryTypeAutocomplete, setInventoryTypeAutocomplete] =
    useState<any>(null)
  const [statusAutocomplete, setStatusAutocomplete] = useState<any>(null)
  const [filterButtonSelected, setFilterButtonSelected] =
    useState<boolean>(true)
  const [selectedRange, setselectedRange] = useState(0)
  const RANGE_ITEMS: RangeType[] = [
    {
      label: getTranslate('View two weeks'),
      value: 'TWO_WEEKS'
    },
    {
      label: getTranslate('View monthly'),
      value: 'MONTH'
    }
  ]
  const [selectedDateInDatePicker, setselectedDateInDatePicker] =
    useState<DateObject>({
      gregorianStartDate: getTodayDate(),
      gregorianEndDate: addDaysToDate(getTodayDate(), 13)
    })

  const [
    getMonitoringAccommodationsData,
    {
      data: accomodationMonitoringUpdate,
      loading: loadingForAccomodationMonitoringUpdate,
      refetch: refetchForAccomodationMonitoringUpdate
    }
  ] = useLazyQuery<{
    getUpdateMonitoringAccommodationLayer: MonitoringUpdateAccommodationLayerResponse
  }>(MONITORING_UPDATE_ACCOMMODATION_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const [
    getMonitoringRoomTypeData,
    { data: roomTypeMonitoringUpdate, loading: loadingForRoomType }
  ] = useLazyQuery<{
    getUpdateMonitoringRoomTypeLayer: MonitoringUpdateRoomTypeLayerResponse
  }>(MONITORING_UPDATE_ROOM_TYPE_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const [
    getMonitoringRatePlanData,
    { data: ratePlanMonitoringUpdate, loading: loadingForRatePlan }
  ] = useLazyQuery<{
    getUpdateMonitoringRatePlanLayer: MonitoringUpdateRatePlanrResponse
  }>(MONITORING_UPDATE_RATE_PLAN_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const handleChangeRange = (newRangeIndex: number) => {
    setselectedRange(newRangeIndex)
    setselectedDateInDatePicker({
      gregorianStartDate: selectedDateInDatePicker.gregorianStartDate,
      gregorianEndDate: addDaysToDate(
        selectedDateInDatePicker.gregorianStartDate!,
        RANGE_ITEMS[newRangeIndex].value === 'TWO_WEEKS' ? 13 : 30
      )
    })
  }

  const selectedDayInDatePickerWeekIndex = useMemo(
    () => moment(selectedDateInDatePicker.gregorianStartDate).day(),
    [selectedDateInDatePicker]
  )
  const weekendIndexes = useMemo(
    () => [
      selectedDayInDatePickerWeekIndex <= 4
        ? 4 - selectedDayInDatePickerWeekIndex
        : 11 - selectedDayInDatePickerWeekIndex,
      selectedDayInDatePickerWeekIndex <= 5
        ? 5 - selectedDayInDatePickerWeekIndex
        : 12 - selectedDayInDatePickerWeekIndex
    ],
    [selectedDateInDatePicker]
  )

  useEffect(() => {
    if (inventoryTypeAutocomplete) {
      getMonitoringAccommodationsData({
        variables: {
          filters: {
            inventoryType:
              inventoryTypeAutocomplete && inventoryTypeAutocomplete.value,
            from: selectedDateInDatePicker.gregorianStartDate,
            to: selectedDateInDatePicker.gregorianEndDate,
            updateHealthStatus: statusAutocomplete
              ? statusAutocomplete.value
              : undefined
          }
        }
      })
    }
  }, [selectedDateInDatePicker])

  return (
    <div>
      <PageHeader title={getTranslate('Update')} />
      <div className={classes.header}>
        <SectionTitle title="Update Monitoring" marginBottom />
        <div className={classes.buttons}>
          <Button
            size={'medium'}
            startIcon={<FilterListIcon />}
            variant="contained"
            color="primary"
            onClick={() => setFilterButtonSelected(!filterButtonSelected)}>
            {getTranslate('Filter')}
          </Button>
        </div>
      </div>
      <Collapse in={filterButtonSelected}>
        <MonitoringUpdateFilters
          selectedDateInDatePicker={selectedDateInDatePicker}
          inventoryTypeAutocomplete={inventoryTypeAutocomplete}
          setInventoryTypeAutocomplete={setInventoryTypeAutocomplete}
          statusAutocomplete={statusAutocomplete}
          setStatusAutocomplete={setStatusAutocomplete}
          setShowChannels={setShowChannels}
          setShowCrsAndPms={setShowCrsAndPms}
          loadingForAccomodationMonitoringUpdate={
            loadingForAccomodationMonitoringUpdate
          }
          getMonitoringAccommodationsData={getMonitoringAccommodationsData}
        />
      </Collapse>
      <Hidden smDown>
        <div className={classes.switchWithTextContainer}>
          <Button
            onClick={() => refetchForAccomodationMonitoringUpdate()}
            className={classes.buttonRefresh}
            size={'medium'}
            startIcon={<RefreshIcon />}
            variant="text"
            color="primary">
            {getTranslate('Refresh')}
          </Button>
          <div>
            <SwitchWithText
              items={[RANGE_ITEMS[0].label, RANGE_ITEMS[1].label]}
              selectedItemIndex={selectedRange}
              onChange={handleChangeRange}
            />
          </div>
        </div>
      </Hidden>
      <InventoryDateRow
        {...{ selectedDateInDatePicker, setselectedDateInDatePicker }}
        range={RANGE_ITEMS[selectedRange].value}
      />

      {loadingForAccomodationMonitoringUpdate ? (
        <MonitoringUpdateSkeleton />
      ) : showCrsAndPms || showChannels ? (
        <InfiniteScroll
          pageStart={0}
          loadMore={() => {
            setTimeout(() => {
              setnumberOfVisibleAccommodations(
                numberOfVisibleAccommodations + PAGE_SIZE
              )
            }, 500)
          }}
          hasMore={
            accomodationMonitoringUpdate &&
            numberOfVisibleAccommodations <=
              accomodationMonitoringUpdate.getUpdateMonitoringAccommodationLayer
                .data.length -
                1
          }
          loader={
            <div key={0} className={classes.loadingContainer}>
              <CircularProgress size={32} />
            </div>
          }
          useWindow={true}>
          {accomodationMonitoringUpdate?.getUpdateMonitoringAccommodationLayer.data
            .slice(0, numberOfVisibleAccommodations)
            .map((data, index) => (
              <MonitoringUpdateBoxRow
                range={RANGE_ITEMS[selectedRange].value}
                data={data}
                key={index}
                weekendIndexes={weekendIndexes}
                showChannels={showChannels}
                roomTypeMonitoringUpdate={
                  roomTypeMonitoringUpdate?.getUpdateMonitoringRoomTypeLayer
                }
                ratePlanMonitoringUpdate={
                  ratePlanMonitoringUpdate?.getUpdateMonitoringRatePlanLayer
                }
                getMonitoringRoomTypeData={getMonitoringRoomTypeData}
                getMonitoringRatePlanData={getMonitoringRatePlanData}
                inventoryTypeAutocomplete={inventoryTypeAutocomplete}
                statusAutocomplete={statusAutocomplete}
                selectedDateInDatePicker={selectedDateInDatePicker}
                loadingForRoomType={loadingForRoomType}
                loadingForRatePlan={loadingForRatePlan}
                accommodationIndex={index}
              />
            ))}
        </InfiniteScroll>
      ) : (
        ''
      )}
    </div>
  )
}
